import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../hooks/useTranslation';
import '../styles/paymentModal.css';

// Static metadata for every payment method. The modal renders only those
// whose `key` is present in the `enabledMethods` prop (admin-controlled).
const METHODS = [
  { key: 'yookassa', icon: 'fa-credit-card', labelKey: 'paymentModal.method.yookassa', hintKey: 'paymentModal.hints.yookassa' },
  { key: 'tribute', icon: 'fa-globe', labelKey: 'paymentModal.method.tribute', hintKey: 'paymentModal.hints.tribute' },
  { key: 'platega', icon: 'fa-credit-card', labelKey: 'paymentModal.method.platega', hintKey: 'paymentModal.hints.platega' },
  { key: 'crypto', icon: 'fa-coins', labelKey: 'paymentModal.method.crypto', hintKey: 'paymentModal.hints.crypto' },
];

const ALL_METHOD_KEYS = METHODS.map((m) => m.key);

const PaymentMethodModal = ({ open, method, onChangeMethod, onConfirm, onCancel, isLoading, allowedMethods, enabledMethods }) => {
  const { t } = useTranslation();
  const allowed = Array.isArray(allowedMethods) && allowedMethods.length
    ? new Set(allowedMethods)
    : null;
  // enabledMethods: admin-controlled visibility. Default to all so behavior is
  // unchanged if the parent did not fetch the flags yet / the request failed.
  const enabled = new Set(
    Array.isArray(enabledMethods) && enabledMethods.length ? enabledMethods : ALL_METHOD_KEYS
  );
  const visibleMethods = METHODS.filter((m) => enabled.has(m.key));

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const currentMethodSelectable = visibleMethods.some((m) => m.key === method) && !(allowed && !allowed.has(method));

  const modalContent = (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('paymentModal.title')}</h3>
        </div>

        {visibleMethods.length === 0 ? (
          <div className="payment-hint">
            <p className="text-center text-muted my-3">{t('paymentModal.noMethods') || 'Нет доступных способов оплаты'}</p>
          </div>
        ) : (
          <>
            <div className="payment-options">
              {visibleMethods.map((m) => {
                const isDisabled = isLoading || (allowed && !allowed.has(m.key));
                return (
                  <label
                    key={m.key}
                    className={`option ${method === m.key ? 'selected' : ''} ${allowed && !allowed.has(m.key) ? 'disabled' : ''}`}
                  >
                    <input
                      type="radio"
                      name="payment-method"
                      value={m.key}
                      checked={method === m.key}
                      onChange={() => onChangeMethod(m.key)}
                      disabled={isDisabled}
                    />
                    <span>{t(m.labelKey)}</span>
                  </label>
                );
              })}
            </div>

            <div className="payment-hint">
              {visibleMethods.map((m) => (
                <div className="hint-row" key={m.key}>
                  <i className={`fas ${m.icon}`} aria-hidden="true"></i>
                  <span>{t(m.hintKey)}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="modal-actions">
          <button className="btn cancel" onClick={onCancel} disabled={isLoading}>
            {t('paymentModal.action.cancel')}
          </button>
          <button
            className={`btn primary ${isLoading ? 'loading' : ''}`}
            onClick={onConfirm}
            disabled={isLoading || !currentMethodSelectable}
          >
            {isLoading ? (
              <>
                <span className="payment-spinner" aria-hidden="true"></span>
                <span>{t('paymentModal.action.loading')}</span>
              </>
            ) : (
              t('paymentModal.action.pay')
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default PaymentMethodModal;
